import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import NotAssignComponent from '../NotAssign.component';
import { Internship } from '../../redux/Internship/Internship.type';
import { DATE_FORMAT } from '../../constants/date.contant';
import { Employee } from '../../redux/Employee/Employee.type';
import UserBadgeComponent from '../UserBadge.component';
import { Job } from '../../redux/Job/Job.type';
import { dateFormatter } from '../../utils/formatters';
import { Person } from '../../redux/Candidate/Candidate.type';
import { StatusColor } from '../../constants/statusColors.constant';
import BadgeComponent from '../Badge.component';
import { Region } from '../../redux/Region/Region.type';

export interface InternshipInfoBlockProps {
  info: Internship;
  peopleList?: Person[];
}

const StyledInternshipInfoTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
`;

const StyledInternshipInfoJob = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
const StyledInternshipInfoRowR = styled.div``;
const StyledInternshipMentors = styled(StyledInternshipInfoRowR)`
  & > div {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
`;
const StyledInternshipInfoRowL = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 150px;
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledInternshipInfoRowLabel = styled.div``;
const StyledInternshipInfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  line-height: 14px;
  font-weight: 500;
`;
const StyledInternshipInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 1rem;
`;

const InternshipInfoBlockComponent = ({ info, peopleList = [] }: InternshipInfoBlockProps) => {
  const { t, i18n } = useTranslation();

  const renderPeopleList = useMemo(() => {
    if (!Array.isArray(peopleList) || peopleList.length === 0) {
      return null;
    }

    return (
      <>
      <StyledInternshipInfoTitle>{t('List of added candidates')}:</StyledInternshipInfoTitle>
      {
        peopleList.map((people: Person) => (
            <StyledInternshipInfoRow key={people.id}>
              <StyledInternshipInfoRowR>
                <NotAssignComponent has={!!people.fullName}>
                  {people.fullName}
                </NotAssignComponent>
              </StyledInternshipInfoRowR>
            </StyledInternshipInfoRow>
        ))
      }
      </>
    );
  }, [peopleList])

  return (
    <StyledInternshipInfo>
      <StyledInternshipInfoRow>
        <StyledInternshipInfoRowL>
          <StyledInternshipInfoRowLabel>
            {t('Jobs')}:
          </StyledInternshipInfoRowLabel>
        </StyledInternshipInfoRowL>
        <StyledInternshipInfoRowR>
          <NotAssignComponent has={!!info.jobs && info.jobs.length > 0}>
            {info.jobs &&
              info.jobs.map((job: Job) => (
                <StyledInternshipInfoJob key={`info-job-${job.id}`}>
                  {job.name}
                </StyledInternshipInfoJob>
              ))}
          </NotAssignComponent>
        </StyledInternshipInfoRowR>
      </StyledInternshipInfoRow>
      <StyledInternshipInfoRow>
        <StyledInternshipInfoRowL>
          <StyledInternshipInfoRowLabel>
            {t('Status')}:
          </StyledInternshipInfoRowLabel>
        </StyledInternshipInfoRowL>
        <StyledInternshipInfoRowR>
          <NotAssignComponent has={!!info.status}>
            <BadgeComponent
              appearance="default"
              color={
                (info.status && info.status.color &&
                  i18n.exists(`statusColor.${info.status.color}`))
                  ? `STATUSES_${info.status.color.toUpperCase()}`
                  : (info.status?.color ||
                  `STATUSES_${StatusColor.GREY.toUpperCase()}`)
              }
              textColor={
                (info.status && info.status.textColor &&
                  i18n.exists(`statusColor.${info.status.textColor}`))
                  ? `STATUSES_${info.status.textColor.toUpperCase()}`
                  : (info.status?.textColor ||
                  `STATUSES_${StatusColor.GREY.toUpperCase()}`)
              }
              text={info.status?.name}
            />
          </NotAssignComponent>
        </StyledInternshipInfoRowR>
      </StyledInternshipInfoRow>
      <StyledInternshipInfoRow>
        <StyledInternshipInfoRowL>
          <StyledInternshipInfoRowLabel>
            {t('Mentors')}:
          </StyledInternshipInfoRowLabel>
        </StyledInternshipInfoRowL>
        <StyledInternshipMentors>
          <NotAssignComponent has={!!info.mentors && info.mentors.length > 0}>
            {info.mentors &&
              info.mentors.map((mentor: Employee) => (
                <UserBadgeComponent
                  key={`info-mentor-${mentor.id}`}
                  info={mentor}
                />
              ))}
          </NotAssignComponent>
        </StyledInternshipMentors>
      </StyledInternshipInfoRow>
      <StyledInternshipInfoRow>
        <StyledInternshipInfoRowL>
          <StyledInternshipInfoRowLabel>
          {t('Tech specialist')}:
          </StyledInternshipInfoRowLabel>
        </StyledInternshipInfoRowL>
        <StyledInternshipMentors>
          <NotAssignComponent has={!!info.techSpecialists && info.techSpecialists.length > 0}>
            {info.techSpecialists &&
              info.techSpecialists.map((techSpecialist: Employee) => (
                <UserBadgeComponent
                  key={`info-tech-specialist-${techSpecialist.id}`}
                  info={techSpecialist}
                />
              ))}
          </NotAssignComponent>
        </StyledInternshipMentors>
      </StyledInternshipInfoRow>
      <StyledInternshipInfoRow>
        <StyledInternshipInfoRowL>
          <StyledInternshipInfoRowLabel>
            {t('Date testing')}:
          </StyledInternshipInfoRowLabel>
        </StyledInternshipInfoRowL>
        <StyledInternshipInfoRowR>
          <NotAssignComponent has={!!info.testingAt}>
            {info.testingAt && dateFormatter(info.testingAt, DATE_FORMAT)}
          </NotAssignComponent>
        </StyledInternshipInfoRowR>
      </StyledInternshipInfoRow>
      <StyledInternshipInfoRow>
        <StyledInternshipInfoRowL>
          <StyledInternshipInfoRowLabel>
            {t('Date start')}:
          </StyledInternshipInfoRowLabel>
        </StyledInternshipInfoRowL>
        <StyledInternshipInfoRowR>
          <NotAssignComponent has={!!info.startAt}>
            {info.startAt && dateFormatter(info.startAt, DATE_FORMAT)}
          </NotAssignComponent>
        </StyledInternshipInfoRowR>
      </StyledInternshipInfoRow>
      <StyledInternshipInfoRow>
        <StyledInternshipInfoRowL>
          <StyledInternshipInfoRowLabel>
            {t('Date end')}:
          </StyledInternshipInfoRowLabel>
        </StyledInternshipInfoRowL>
        <StyledInternshipInfoRowR>
          <NotAssignComponent has={!!info.endAt}>
            {info.endAt && dateFormatter(info.endAt, DATE_FORMAT)}
          </NotAssignComponent>
        </StyledInternshipInfoRowR>
      </StyledInternshipInfoRow>
      <StyledInternshipInfoRow>
        <StyledInternshipInfoRowL>
          <StyledInternshipInfoRowLabel>
            {t('Number of people')}:
          </StyledInternshipInfoRowLabel>
        </StyledInternshipInfoRowL>
        <StyledInternshipInfoRowR>
          <NotAssignComponent has={info.count !== undefined && info.count !== null}>
            {info.count}
          </NotAssignComponent>
        </StyledInternshipInfoRowR>
      </StyledInternshipInfoRow>
      <StyledInternshipInfoRow>
        <StyledInternshipInfoRowL>
          <div>{t('Region')}:</div>
        </StyledInternshipInfoRowL>
        <StyledInternshipInfoRowR>
          {info.regions?.length ? info.regions.map((region: Region) => (
            <div key={region.id}>{region.name}</div>
          )) : '-'}
        </StyledInternshipInfoRowR>

      </StyledInternshipInfoRow>
      {renderPeopleList}
    </StyledInternshipInfo>
  );
};

export default InternshipInfoBlockComponent;
